import { useState, useEffect } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { dashboardApi } from '../api'
import { useApp } from '../AppContext'
import { StatCard, BadgeStatut, BadgeType, fmt, fmtDate, Loading } from '../components/ui'

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']

export default function Dashboard() {
  const { toast } = useApp()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    dashboardApi.stats()
      .then(r => setData(r.data))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="content"><Loading /></div>
  if (!data) return null

  const evol = data.ca_mois_prec > 0
    ? ((data.ca_mois - data.ca_mois_prec) / data.ca_mois_prec * 100).toFixed(1)
    : null

  const chartData = (data.ca_mensuel || []).map(m => ({
    name: MOIS[m.mois - 1],
    'CA TTC': parseFloat(m.ca_ttc).toFixed(2),
  }))

  return (
    <div className="content">
      {/* KPIs */}
      <div className="grid4" style={{ marginBottom: 18 }}>
        <StatCard
          label="CA ce mois"
          value={fmt(data.ca_mois)}
          icon="report-money"
          trend={evol ? { up: evol > 0, label: `${evol > 0 ? '+' : ''}${evol}% vs mois préc.` } : null}
        />
        <StatCard
          label="Impayés"
          value={fmt(data.impayes?.total)}
          icon="clock-dollar"
          color="danger"
        />
        <StatCard
          label="En retard"
          value={`${data.retard?.nb || 0} facture(s)`}
          icon="alert-triangle"
          color="danger"
        />
        <StatCard
          label="Alertes actives"
          value={data.alertes || 0}
          icon="bell"
          color={data.alertes > 0 ? 'danger' : 'primary'}
        />
      </div>

      <div className="grid2" style={{ marginBottom: 18 }}>
        {/* Graphique CA */}
        <div className="card">
          <div className="card-title">Évolution du CA (12 mois)</div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCA" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2e7d52" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2e7d52" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text3)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--text3)' }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={v => fmt(v)} labelStyle={{ color: 'var(--text)' }} />
                <Area type="monotone" dataKey="CA TTC" stroke="#2e7d52" strokeWidth={2} fill="url(#colorCA)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)' }}>
              Pas encore de données
            </div>
          )}
        </div>

        {/* Top clients */}
        <div className="card">
          <div className="card-title">Top clients (année)</div>
          {data.top_clients?.length ? (
            <div>
              {data.top_clients.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: i < data.top_clients.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    {i + 1}
                  </div>
                  <span style={{ flex: 1, fontSize: 13 }}>{c.nom}</span>
                  <strong style={{ fontSize: 13 }}>{fmt(c.total)}</strong>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Aucun client cette année</div>
          )}
        </div>
      </div>

      {/* Derniers documents */}
      <div className="card" style={{ padding: 0 }}>
        <div className="card-title" style={{ padding: '14px 18px 0' }}>Derniers documents</div>
        {data.derniers_docs?.length ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Numéro</th><th>Type</th><th>Contact</th>
                  <th>Date</th><th>Montant TTC</th><th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.derniers_docs.map(d => (
                  <tr key={d.id}>
                    <td><strong>{d.numero}</strong></td>
                    <td><BadgeType type={d.type} /></td>
                    <td>{d.contact_nom}</td>
                    <td>{fmtDate(d.date_document)}</td>
                    <td><strong>{fmt(d.total_ttc)}</strong></td>
                    <td><BadgeStatut statut={d.statut} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
            Aucun document pour l'instant
          </div>
        )}
      </div>
    </div>
  )
}
